import type { AfipEnvironment } from '@/api/types';

/**
 * Modelo de producto del rediseño (F0). Una organización puede contratar varios productos
 * (cuenta híbrida). El `ProductSwitcher` permite moverse entre las experiencias contratadas.
 * Platform Admin es consola interna (no comercial) — sólo visible para SuperAdmin.
 *
 * Ver `docs/design/` (Plan de Rediseño · §01 Comercial / §04 Navegación).
 */
export type TildeProduct = 'Empresa' | 'Estudio' | 'Api' | 'Admin';

export interface ProductMeta {
  /** Etiqueta visible (es-AR). */
  label: string;
  /** Descriptor corto del comprador / propósito. */
  caption: string;
  /** Color de identidad del producto (clave de paleta MUI o hex). Admin usa ámbar (interno). */
  dotColor: 'primary' | 'secondary';
}

export const PRODUCT_META: Record<TildeProduct, ProductMeta> = {
  Empresa: { label: 'Monotributo', caption: 'Facturación simple', dotColor: 'primary' },
  Estudio: { label: 'Estudio Contable', caption: 'Cartera multi-CUIT', dotColor: 'primary' },
  Api: { label: 'API para Programadores', caption: 'Integración', dotColor: 'primary' },
  Admin: { label: 'Platform Admin', caption: 'Interno', dotColor: 'secondary' },
};

/**
 * Ambiente operativo del rediseño. Canónico Sandbox/Production (terminología API). El dominio
 * AFIP usa `Homologation`/`Production` (ver `AfipEnvironment`); `mapAfipEnvironment` traduce.
 */
export type TildeEnvironment = 'Sandbox' | 'Production';

export function mapAfipEnvironment(env: AfipEnvironment): TildeEnvironment {
  return env === 'Production' ? 'Production' : 'Sandbox';
}

/**
 * Formatea un CUIT de 11 dígitos a `XX-XXXXXXXX-X`. Si no tiene 11 dígitos, lo devuelve tal cual
 * (no asumimos formato para no romper datos sucios).
 */
export function formatCuit(cuit: string): string {
  const digits = cuit.replace(/\D/g, '');
  if (digits.length !== 11) return cuit;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}
